import React, { useState, useEffect } from 'react';
import { useStrategyOperations } from '../hooks/useStrategyOperations';
import { SignalType, SignalAction, FScore } from '../types';
import { profileService } from '../services/profileService';

const Reports: React.FC = () => {
  const { signals, loading, error, config, getLatestSignals } = useStrategyOperations();
  const [reportType, setReportType] = useState<'all' | 'buy' | 'sell'>('all');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  
  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      try {
        const isAuth = await profileService.isAuthenticated();
        setIsAuthenticated(isAuth);
      } catch (error) {
        console.error('Error checking authentication:', error);
      }
    };
    
    checkAuth();
  }, []);
  
  // Ensure signals is initialized as an array
  useEffect(() => {
    getLatestSignals();
  }, [getLatestSignals]);
  
  // Ensure signals is always an array for safety
  const signalsArray = Array.isArray(signals) ? signals : [];
  
  // Filter signals based on report type
  const filteredSignals = signalsArray.filter(signal => {
    if (reportType === 'all') return true;
    if (reportType === 'buy') return signal.finalSignal === 'Buy';
    if (reportType === 'sell') return signal.finalSignal === 'Sell';
    return true;
  });
  
  // Generate summary statistics
  const totalSignals = signalsArray.length;
  const buySignals = signalsArray.filter(s => s.finalSignal === 'Buy').length;
  const sellSignals = signalsArray.filter(s => s.finalSignal === 'Sell').length;
  const holdSignals = signalsArray.filter(s => s.finalSignal === 'Hold').length;
  
  const buyPercentage = totalSignals > 0 ? (buySignals / totalSignals * 100).toFixed(1) : '0.0';
  const sellPercentage = totalSignals > 0 ? (sellSignals / totalSignals * 100).toFixed(1) : '0.0';
  const holdPercentage = totalSignals > 0 ? (holdSignals / totalSignals * 100).toFixed(1) : '0.0';
  
  // Helper function to get background color based on signal type
  const getSignalColor = (signal?: SignalType | SignalAction): string => {
    switch (signal) {
      case 'buy':
      case 'Buy':
        return 'bg-green-100 text-green-800';
      case 'sell':
      case 'Sell':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Generate report summary table data
  const getBuySummaryData = () => {
    const buySignals = signalsArray.filter(s => s.finalSignal === 'Buy');
    
    // Average CAPE and F-Score
    const avgCape = buySignals.reduce((sum, s) => sum + (s.cape || 0), 0) / (buySignals.length || 1);
    const avgFScore = buySignals.reduce((sum, s) => {
      if (s.fScore && typeof s.fScore === 'object' && 'score' in s.fScore) {
        return sum + s.fScore.score;
      }
      return sum;
    }, 0) / (buySignals.length || 1);
    
    // CAPE range
    const capeValues = buySignals.map(s => s.cape).filter((c): c is number => c !== undefined);
    const minCape = capeValues.length > 0 ? Math.min(...capeValues) : Number.MAX_VALUE;
    const maxCape = capeValues.length > 0 ? Math.max(...capeValues) : 0;
    
    // F-Score distribution
    const fScoreDistribution = [0, 0, 0, 0]; // [0-3, 4-5, 6-7, 8-9]
    buySignals.forEach(s => {
      if (s.fScore && typeof s.fScore === 'object' && 'score' in s.fScore) {
        const score = s.fScore.score;
        if (score <= 3) fScoreDistribution[0]++;
        else if (score <= 5) fScoreDistribution[1]++;
        else if (score <= 7) fScoreDistribution[2]++;
      else fScoreDistribution[3]++;
      }
    });
    
    return {
      avgCape: isNaN(avgCape) ? 'N/A' : avgCape.toFixed(2),
      avgFScore: isNaN(avgFScore) ? 'N/A' : avgFScore.toFixed(1),
      capeRange: minCape === Number.MAX_VALUE ? 'N/A' : `${minCape.toFixed(2)} - ${maxCape.toFixed(2)}`,
      fScoreDistribution
    };
  };
  
  const getSellSummaryData = () => {
    const sellSignals = signalsArray.filter(s => s.finalSignal === 'Sell');
    
    // Average CAPE and F-Score
    const avgCape = sellSignals.reduce((sum, s) => sum + (s.cape || 0), 0) / (sellSignals.length || 1);
    const avgFScore = sellSignals.reduce((sum, s) => {
      if (s.fScore && typeof s.fScore === 'object' && 'score' in s.fScore) {
        return sum + s.fScore.score;
      }
      return sum;
    }, 0) / (sellSignals.length || 1);
    
    // CAPE range
    const capeValues = sellSignals.map(s => s.cape).filter((c): c is number => c !== undefined);
    const minCape = capeValues.length > 0 ? Math.min(...capeValues) : Number.MAX_VALUE;
    const maxCape = capeValues.length > 0 ? Math.max(...capeValues) : 0;
    
    // F-Score distribution
    const fScoreDistribution = [0, 0, 0, 0]; // [0-3, 4-5, 6-7, 8-9]
    sellSignals.forEach(s => {
      if (s.fScore && typeof s.fScore === 'object' && 'score' in s.fScore) {
        const score = s.fScore.score;
        if (score <= 3) fScoreDistribution[0]++;
        else if (score <= 5) fScoreDistribution[1]++;
        else if (score <= 7) fScoreDistribution[2]++;
      else fScoreDistribution[3]++;
      }
    });
    
    return {
      avgCape: isNaN(avgCape) ? 'N/A' : avgCape.toFixed(2),
      avgFScore: isNaN(avgFScore) ? 'N/A' : avgFScore.toFixed(1),
      capeRange: minCape === Number.MAX_VALUE ? 'N/A' : `${minCape.toFixed(2)} - ${maxCape.toFixed(2)}`,
      fScoreDistribution
    };
  };

  // Get summary data based on report type
  const summaryData = reportType === 'sell' ? getSellSummaryData() : getBuySummaryData();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Trading Strategy Reports</h1>
        {!isAuthenticated && (
          <a 
            href="/login" 
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
          >
            Login
          </a>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Configuration Summary */}
      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Strategy Configuration</h2>
        <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <h3 className="text-sm font-medium text-gray-500">CAPE Lookback Period</h3>
            <p className="mt-1 text-base text-gray-900">{config.capeLookbackYears} years</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">CAPE EMA Span</h3>
            <p className="mt-1 text-base text-gray-900">{config.capeEmaSpan} quarters</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">P&F Pattern Type</h3>
            <p className="mt-1 text-base text-gray-900 capitalize">{config.pnfPatternType} Top/Bottom</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">P&F Box Size</h3>
            <p className="mt-1 text-base text-gray-900">{(config.pnfBoxSizePercent * 100).toFixed(2)}%</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">P&F Reversal Boxes</h3>
            <p className="mt-1 text-base text-gray-900">{config.pnfReversalBoxes} boxes</p>
          </div>
        </div>
      </div>

      {/* Signal Summary */}
      <div className="card">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Signal Summary</h2>
        <div className="grid grid-cols-1 gap-y-4 gap-x-6 sm:grid-cols-3">
          <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-green-500">
            <h3 className="text-sm font-medium text-gray-500">Buy Signals</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{buySignals} <span className="text-sm text-gray-500">({buyPercentage}%)</span></p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-red-500">
            <h3 className="text-sm font-medium text-gray-500">Sell Signals</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{sellSignals} <span className="text-sm text-gray-500">({sellPercentage}%)</span></p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border-l-4 border-gray-500">
            <h3 className="text-sm font-medium text-gray-500">Hold Signals</h3>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{holdSignals} <span className="text-sm text-gray-500">({holdPercentage}%)</span></p>
          </div>
        </div>
      </div>

      {/* Report Type Selection */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">Detailed Report</h2>
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${reportType === 'all' ? 'bg-primary-100 text-primary-800' : 'bg-white border text-gray-600'}`}
              onClick={() => setReportType('all')}
            >
              All Signals
            </button>
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${reportType === 'buy' ? 'bg-green-100 text-green-800' : 'bg-white border text-gray-600'}`}
              onClick={() => setReportType('buy')}
            >
              Buy Signals
            </button>
            <button
              className={`px-3 py-1 rounded text-sm font-medium ${reportType === 'sell' ? 'bg-red-100 text-red-800' : 'bg-white border text-gray-600'}`}
              onClick={() => setReportType('sell')}
            >
              Sell Signals
            </button>
          </div>
        </div>

        {/* Signal Statistics */}
        {(reportType === 'buy' || reportType === 'sell') && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-md font-medium text-gray-900 mb-2">{reportType === 'buy' ? 'Buy' : 'Sell'} Signal Statistics</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-sm text-gray-500">Average CAPE</p>
                <p className="text-lg font-medium">{summaryData.avgCape}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Average F-Score</p>
                <p className="text-lg font-medium">{summaryData.avgFScore}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">CAPE Range</p>
                <p className="text-lg font-medium">{summaryData.capeRange}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">F-Score Distribution</p>
                <div className="flex space-x-1 mt-1">
                  <div className="text-xs">
                    <div className="h-4 w-6 bg-red-500" style={{ height: `${(summaryData.fScoreDistribution[0] * 24)}px` }}></div>
                    <div className="text-center">0-3</div>
                  </div>
                  <div className="text-xs">
                    <div className="h-4 w-6 bg-yellow-500" style={{ height: `${(summaryData.fScoreDistribution[1] * 24)}px` }}></div>
                    <div className="text-center">4-5</div>
                  </div>
                  <div className="text-xs">
                    <div className="h-4 w-6 bg-blue-500" style={{ height: `${(summaryData.fScoreDistribution[2] * 24)}px` }}></div>
                    <div className="text-center">6-7</div>
                  </div>
                  <div className="text-xs">
                    <div className="h-4 w-6 bg-green-500" style={{ height: `${(summaryData.fScoreDistribution[3] * 24)}px` }}></div>
                    <div className="text-center">8-9</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Signals Table */}
        {loading ? (
          <div className="text-center py-10">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-primary-600">Loading signals...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticker</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CAPE</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CAPE Percentile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">F-Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preliminary Signal</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Final Signal</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSignals.map((signal) => (
                  <tr key={signal.ticker}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{signal.ticker}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{signal.cape ? signal.cape.toFixed(2) : 'N/A'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{signal.capePercentile ? signal.capePercentile.toFixed(1) : 'N/A'}%</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {signal.fScore && typeof signal.fScore === 'object' && 'score' in signal.fScore 
                        ? `${signal.fScore.score}/9` 
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSignalColor(signal.preliminarySignal)}`}>
                        {signal.preliminarySignal ? signal.preliminarySignal.toUpperCase() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSignalColor(signal.finalSignal)}`}>
                        {signal.finalSignal ? signal.finalSignal.toUpperCase() : 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredSignals.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                      No signals found for the selected filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports; 